import { Component, OnInit, OnDestroy, Inject } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { UserserviceService } from "src/app/Services/userservice.service";
import { ToastrService } from "ngx-toastr";
import { AuthModalService } from "src/app/Services/auth-modal.service";
import { EventEmitterService } from "src/app/Services/event-emitter.service";
import { Subscription } from "rxjs";
import { getOrCreateDeviceId, getCurrentPositionOptional, isGeoLocationDenialCode, savePendingFlowAccessRequest } from "src/app/shared/utils/device-access.util";

@Component({
  selector: "app-flow-access-blocker",
  templateUrl: "./flow-access-blocker.component.html",
  styleUrls: ["./flow-access-blocker.component.scss"],
})
export class FlowAccessBlockerComponent implements OnInit, OnDestroy {
  userData: any;
  flowData: any;
  isPrivate: boolean = false;
  isRestricted: boolean = false;
  isAdvanced: boolean = false;
  hasAccess: boolean = false;
  showAccessRequest: boolean = false;
  isSubmitting: boolean = false;
  isLoading: boolean = true;
  isLocating = false;
  denialCode = "";
  denialMessage = "";
  deviceId = "";
  /** Request access is supported for guides/flows only, not bundles. */
  allowRequestAccess = true;
  private loginSubscription?: Subscription;

  constructor(
    private flowServiceService: FlowServiceService,
    private userserviceService: UserserviceService,
    private router: Router,
    public dialogRef: MatDialogRef<FlowAccessBlockerComponent>,
    private toastr: ToastrService,
    private authModalService: AuthModalService,
    private eventEmitterService: EventEmitterService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.flowData = JSON.parse(localStorage.getItem("flowDataKey"));
  }

  ngOnInit() {
    this.userData = JSON.parse(localStorage.getItem("LoggedInUser"));
    this.deviceId = getOrCreateDeviceId();
    if (this.data) {
      this.isPrivate = !!this.data.isPrivate;
      this.isRestricted =
        !!this.data.isRestricted ||
        (!!this.data.isAdvanced && !this.data.isPrivate);
      this.isAdvanced = !!this.data.isAdvanced;
      this.hasAccess = !!this.data.hasAccess;
      this.denialCode = this.data.denialCode || "";
      this.denialMessage = this.data.denialMessage || this.defaultMessageForCode();
      this.allowRequestAccess = this.data.allowRequestAccess !== false;
      this.isLoading = false;
      if (this.hasAccess) {
        this.dialogRef.close(true);
        return;
      }
    } else {
      setTimeout(() => {
        this.isLoading = false;
        this.dialogRef.close();
      }, 1000);
    }

    this.loginSubscription =
      this.eventEmitterService.setLoggedInUserDataProperty.subscribe(
        (loggedIn) => {
          if (loggedIn) {
            this.refreshLoggedInUser();
          }
        }
      );
  }

  ngOnDestroy(): void {
    this.loginSubscription?.unsubscribe();
  }

  private refreshLoggedInUser(): void {
    try {
      this.userData = JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    } catch {
      this.userData = null;
    }
  }

  private defaultMessageForCode(): string {
    switch (this.denialCode) {
      case "NETWORK_BLOCKED":
        return "This flow is only available on the allowed company network.";
      case "GEO_BLOCKED":
        return "This flow is only available inside the allowed geographic area.";
      case "GEO_LOCATION_REQUIRED":
        return "Allow location access so we can confirm you are inside the allowed area.";
      case "DEVICE_NOT_ALLOWED":
        return "This flow can only be opened from a registered device.";
      case "COMPANY_EMAIL_REQUIRED":
        return "This flow is limited to approved company email domains.";
      case "PLAN_UPGRADE_REQUIRED":
        return "Advanced access controls require a Growth or Enterprise plan.";
      case "ACCESS_METHODS_FAILED":
        return "You must satisfy all enabled access requirements to view this flow.";
      case "NOT_IN_ALLOWLIST":
        return "You are not on the allowed users list for this flow.";
      case "AUTH_REQUIRED":
        return "Sign in to access this flow via the user list.";
      case "ORG_MEMBERS_ONLY":
        return "This content is only available to members of its organization.";
      default:
        return "You do not have access to this flow.";
    }
  }

  /** Restricted guides/flows (not bundles) can request access from the owner. */
  get canShowRequestAccessAction(): boolean {
    if (!this.allowRequestAccess || this.isSubmitting) {
      return false;
    }
    if (this.isPrivate && !this.isRestricted) {
      return false;
    }
    if (
      this.denialCode === "PLAN_UPGRADE_REQUIRED" ||
      this.denialCode === "PRIVATE" ||
      this.denialCode === "ORG_MEMBERS_ONLY"
    ) {
      return false;
    }
    return this.isRestricted;
  }

  get requestAccessButtonLabel(): string {
    if (this.isSubmitting) {
      return "Requesting...";
    }
    return this.userData ? "Request Access" : "Sign In to Request Access";
  }

  requestAccessOrSignIn(): void {
    if (this.userData) {
      this.requestAccess();
      return;
    }
    this.openLoginModalForAccessRequest();
  }

  private openLoginModalForAccessRequest(): void {
    const flowId = this.data?.flowId || this.flowData?.rowKey;
    if (flowId) {
      savePendingFlowAccessRequest({
        flowId,
        denialCode: this.denialCode,
        denialMessage: this.denialMessage,
        isRestricted: this.isRestricted,
        isPrivate: this.isPrivate,
        isAdvanced: this.isAdvanced,
      });
    }
    this.authModalService.openModal("login", this.router.url);
  }

  get advancedTitle(): string {
    switch (this.denialCode) {
      case "NETWORK_BLOCKED":
        return "Company network required";
      case "GEO_BLOCKED":
        return "Outside allowed area";
      case "GEO_LOCATION_REQUIRED":
        return "Location required";
      case "DEVICE_NOT_ALLOWED":
        return "Device not registered";
      case "COMPANY_EMAIL_REQUIRED":
        return "Company email required";
      case "ACCESS_METHODS_FAILED":
        return "Access requirements not met";
      case "NOT_IN_ALLOWLIST":
        return "Not on user list";
      case "AUTH_REQUIRED":
        return "Sign in required";
      case "ORG_MEMBERS_ONLY":
        return "Organization members only";
      default:
        return "Access blocked";
    }
  }

  showPrivateBlocker() {}

  requestAccess() {
    if (this.isSubmitting) return;

    const flowId = this.flowData?.rowKey;
    if (!flowId) {
      this.toastr.error("Missing flow information.");
      return;
    }

    const requesterEmail = this.userData?.emailAddress || "";
    const requesterName =
      this.userData?.firstName || this.userData?.userName || "";
    const requesterUserName = this.userData?.userName || "";

    const requestData = {
      flowId: flowId,
      flowOwnerEmail: this.flowData?.customerEmail || null,
      contentTitle: this.flowData?.title || null,
      contentType: this.flowData?.type ?? "Bundle",
      requesterEmail: requesterEmail,
      requesterName: requesterName,
      requesterUserName: requesterUserName,
      requestMessage: "Please grant me access to this flow.",
    };

    this.isSubmitting = true;
    this.flowServiceService.requestFlowAccess(requestData).subscribe(
      (response) => {
        this.toastr.success("Access request submitted successfully!");
        this.isSubmitting = false;
        this.dialogRef.close("request_access");
      },
      (error) => {
        console.error("Error submitting access request:", error);
        const message =
          error?.error?.message ||
          "Failed to submit access request. Please try again.";
        this.toastr.error(message);
        this.isSubmitting = false;
      }
    );
  }

  get showShareLocation(): boolean {
    return isGeoLocationDenialCode(this.denialCode);
  }

  shareLocation() {
    if (this.isLocating) {
      return;
    }
    this.isLocating = true;
    getCurrentPositionOptional(15000).then((coords) => {
      this.isLocating = false;
      if (!coords) {
        this.toastr.error(
          "Allow location access in this browser, then try again."
        );
        return;
      }
      this.dialogRef.close({ action: "share_location", coords });
    });
  }

  goToHome() {
    this.dialogRef.close("go_home");
  }

  goToLogin() {
    this.authModalService.openModal("login", this.router.url);
  }

  goToPricing() {
    this.router.navigate(["/pricing"]);
    this.dialogRef.close();
  }

  close() {
    this.dialogRef.close();
  }
}
